import React, { useState, useEffect } from "react";
import axios from "axios";
import Constant from "../Constant";
import DeleteConfirmationModal from "../Common/DeleteConfirmationModal";
import Notification from "../Common/Notification";
import Pagination from "../Common/Pagination";
import CreateFeatureModal from "./CreateFeatureModal";
import EditFeatureModal from "./EditFeatureModal";

const Features = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [featuresPerPage, setFeaturesPerPage] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Show notification helper
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Fetch features on component mount
  useEffect(() => {
    fetchFeatures();
  }, [currentPage, featuresPerPage]);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Constant.apiBasePath}features/all`);
      setFeatures(response.data);
      setLoading(false);
    } catch (error) {
      showNotification("Failed to fetch features", "error");
      console.error("Error fetching features:", error);
      setLoading(false);
    }
  };

  // Get current features for pagination
  const indexOfLastFeature = currentPage * featuresPerPage;
  const indexOfFirstFeature = indexOfLastFeature - featuresPerPage;
  const currentFeatures = features.slice(
    indexOfFirstFeature,
    indexOfLastFeature
  );

  // Change page
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle edit feature
  const handleEditClick = (feature) => {
    setSelectedFeature(feature);
    setShowEditModal(true);
  };

  // Handle delete feature
  const handleDeleteClick = (feature) => {
    setSelectedFeature(feature);
    setShowDeleteModal(true);
  };

  // Delete feature
  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${Constant.apiBasePath}features/${selectedFeature._id}`
      );
      showNotification("Feature deleted successfully");
      setShowDeleteModal(false);
      fetchFeatures();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        showNotification(error.response.data.message, "error");
      } else {
        showNotification("Failed to delete feature", "error");
      }
      console.error("Error deleting feature:", error);
    }
  };

  const handleLimitChange = (e) => {
    setFeaturesPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing limit
  };

  return (
    <div className="content-page">
      <div className="content">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {notification.show && (
                  <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() =>
                      setNotification({ show: false, message: "", type: "" })
                    }
                  />
                )}

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="page-title-heading">Features Management</h4>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <span className="me-1">+</span> Create Feature
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden"></span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table dt-responsive nowrap">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentFeatures.length > 0 ? (
                            currentFeatures.map((feature) => (
                              <tr key={feature._id}>
                                <td>{feature.name}</td>
                                <td>{feature.description}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      feature.isActive
                                        ? "bg-success"
                                        : "bg-warning"
                                    }`}
                                  >
                                    {feature.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td>
                                  <div className="btn-group">
                                    <button
                                      onClick={() => handleEditClick(feature)}
                                      className="btn btn-primary btn-sm me-2 mr-2"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteClick(feature)}
                                      className="btn btn-danger btn-sm"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No features found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        <select
                          className="form-select form-select-sm"
                          value={featuresPerPage}
                          onChange={handleLimitChange}
                        >
                          <option value="5">5 per page</option>
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                        </select>
                      </div>

                      <Pagination
                        itemsPerPage={featuresPerPage}
                        totalItems={features.length}
                        paginate={handlePageChange}
                        currentPage={currentPage}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Feature Modal */}
      <CreateFeatureModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onFeatureCreated={fetchFeatures}
        showNotification={showNotification}
      />

      {/* Edit Feature Modal */}
      {selectedFeature && (
        <EditFeatureModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          feature={selectedFeature}
          onFeatureUpdated={fetchFeatures}
          showNotification={showNotification}
        />
      )}

      {/* Delete Confirmation Modal */}
      {selectedFeature && (
        <DeleteConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Feature"
          message={`Are you sure you want to delete the feature "${selectedFeature.name}"? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default Features;
